import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { PiUser } from 'react-icons/pi';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import AccountAvatar from '#/modules/infra/layout/AccountAvatar';
import { resolveAccountDisplay } from '#/modules/infra/layout/resolveAccountDisplay';

/**
 * 右上角帳號選單：觸發列「您好，{暱稱}」；下拉含頭像／姓名／職稱／編輯個人資訊。
 * 本包不含登出列。
 */
const AccountMenu = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.infra.user);
  const roles = useSelector((state) => state.infra.roles);
  const display = resolveAccountDisplay(user, roles);
  const [open, setOpen] = useState(false);

  const greeting = `您好，${display.displayName}`;

  const handleEditProfile = useCallback(() => {
    setOpen(false);
    navigate(ROUTES.personalCenterPanel('profile'));
  }, [navigate]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label={greeting}
          className={cn(
            'px-3 h-9 max-w-56',
            'bg-primary-100 text-primary-800',
            'font-normal',
            'border-primary-200 rounded-full',
            'justify-start gap-2',
            'hover:bg-primary-200 hover:text-primary-800',
            'aria-expanded:bg-primary-100 aria-expanded:text-primary-800',
            'hover:aria-expanded:bg-primary-200',
          )}
        >
          {display.avatarUrl ? (
            <img
              src={display.avatarUrl}
              alt=""
              className="size-6 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span
              className={cn(
                'size-6 shrink-0',
                'bg-primary-500 text-white',
                'rounded-full',
                'inline-flex items-center justify-center',
              )}
              aria-hidden
            >
              <PiUser className="size-3.5" />
            </span>
          )}
          <span className="truncate text-sm">{greeting}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-3">
        <div className="flex flex-col items-center gap-1.5 pb-3 text-center">
          <AccountAvatar display={display} size="md" />
          <p className="w-full truncate text-sm font-medium text-foreground">
            {display.name}
          </p>
          <p className="w-full truncate text-xs text-muted-foreground">
            {display.title}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleEditProfile}
        >
          編輯個人資訊
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountMenu;
